import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Play, Pause, RotateCcw, Settings, Volume2, VolumeX, Clock, Phone, User, Bot, ChevronDown, ChevronUp, History, Trash2, Eye, EyeOff, X, Printer, Shuffle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

// Escape user-generated content before injecting into print windows (XSS guard)
const escPrint = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const RAOTraining = ({ studentInfo }) => {
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentLineIndex, setCurrentLineIndex] = useState(-1);
  const [displayedLines, setDisplayedLines] = useState([]);
  const [speed, setSpeed] = useState(3000);
  const [showSettings, setShowSettings] = useState(false);
  const [serviceFilter, setServiceFilter] = useState('all');
  const [studentName, setStudentName] = useState(
    studentInfo ? `${studentInfo.first_name} ${studentInfo.last_name}` : ''
  );
  const [showNameInput, setShowNameInput] = useState(true);
  const [currentSession, setCurrentSession] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [raoSessions, setRaoSessions] = useState([]);
  const [selectedHistorySession, setSelectedHistorySession] = useState(null);
  const [sessionCompleted, setSessionCompleted] = useState(false);
  const [generatingVariant, setGeneratingVariant] = useState(false);
  const [difficulty, setDifficulty] = useState('debutant');
  const [isRandomMode, setIsRandomMode] = useState(false);
  const [randomCategory, setRandomCategory] = useState(null);
  const [selectedSessionIds, setSelectedSessionIds] = useState(new Set());
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [hideCitizenText, setHideCitizenText] = useState(false);
  const [allowedLevels, setAllowedLevels] = useState(['debutant', 'intermediaire', 'avance']);
  const playIntervalRef = useRef(null);
  const messagesEndRef = useRef(null);
  const advanceTimeoutRef = useRef(null);
  const isPlayingRef = useRef(false);

  // Performance tracking
  const [pauseCount, setPauseCount] = useState(0);
  const [totalPauseTime, setTotalPauseTime] = useState(0);
  const [restartCount, setRestartCount] = useState(0);
  const pauseStartRef = useRef(null);
  const hasStartedRef = useRef(false);

  const speedOptions = [
    { label: 'Très lent (6s)', value: 6000 },
    { label: 'Lent (4s)', value: 4000 },
    { label: 'Normal (3s)', value: 3000 },
    { label: 'Rapide (2s)', value: 2000 },
    { label: 'Très rapide (1.5s)', value: 1500 },
  ];

  useEffect(() => {
    fetchRAOScenarios();
    fetchRAOSessions();
    // Fetch allowed RAO levels
    if (studentInfo?.student_number) {
      axios.get(`${API}/rao/student/${studentInfo.student_number}/allowed-levels`)
        .then(resp => {
          const levels = resp.data?.levels || ['debutant', 'intermediaire', 'avance'];
          setAllowedLevels(levels);
          // If current difficulty is not allowed, set to the first allowed one
          if (!levels.includes('debutant')) {
            setDifficulty(levels[0] || 'debutant');
          }
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [displayedLines]);

  // Cleanup TTS on unmount
  useEffect(() => {
    return () => {
      window.speechSynthesis?.cancel();
      if (advanceTimeoutRef.current) clearTimeout(advanceTimeoutRef.current);
    };
  }, []);

  // Get best French voice
  const getFrenchVoice = () => {
    const voices = window.speechSynthesis?.getVoices() || [];
    return voices.find(v => v.lang === 'fr-CA') 
      || voices.find(v => v.lang.startsWith('fr')) 
      || null;
  };

  // Speak citizen line using OpenAI TTS API (realistic voice)
  const digitToFrench = { '0': 'zéro', '1': 'un', '2': 'deux', '3': 'trois', '4': 'quatre', '5': 'cinq', '6': 'six', '7': 'sept', '8': 'huit', '9': 'neuf' };

  const formatTextForTTS = (text) => {
    // Convert phone numbers to French words
    let result = text.replace(/\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g, (match) => {
      const digits = match.replace(/\D/g, '');
      const part1 = digits.slice(0, 3).split('').map(d => digitToFrench[d]).join(' ');
      const part2 = digits.slice(3, 6).split('').map(d => digitToFrench[d]).join(' ');
      const part3 = digits.slice(6).split('').map(d => digitToFrench[d]).join(' ');
      return part1 + ', ' + part2 + ', ' + part3;
    });
    // Convert standalone numbers to French words
    result = result.replace(/\b(\d+)\b/g, (match) => {
      if (match.length <= 4) {
        return match.split('').map(d => digitToFrench[d] || d).join(' ');
      }
      return match;
    });
    return result;
  };

  const speakCitizenLine = (text) => {
    return new Promise(async (resolve) => {
      try {
        const resp = await axios.post(`${API}/tts`, {
          text: formatTextForTTS(text),
          voice: 'echo',
          speed: 0.95
        });
        
        if (resp.data?.audio) {
          const audio = new Audio(`data:audio/mp3;base64,${resp.data.audio}`);
          audio.onended = () => resolve();
          audio.onerror = () => resolve();
          // Safety timeout based on text length
          const safetyMs = Math.min(30000, Math.max(3000, text.length * 100));
          const timeout = setTimeout(() => resolve(), safetyMs);
          audio.onended = () => { clearTimeout(timeout); resolve(); };
          audio.onerror = () => { clearTimeout(timeout); resolve(); };
          audio.play().catch(() => resolve());
        } else {
          resolve();
        }
      } catch (e) {
        console.error('TTS error, falling back:', e);
        // Fallback to browser speech synthesis
        if (window.speechSynthesis) {
          try { window.speechSynthesis.cancel(); } catch(err) { /* ignore */ }
          const utterance = new SpeechSynthesisUtterance(formatTextForTTS(text));
          utterance.lang = 'fr-CA';
          const voice = getFrenchVoice();
          if (voice) utterance.voice = voice;
          utterance.rate = 1.0;
          let resolved = false;
          const done = () => { if (!resolved) { resolved = true; resolve(); } };
          utterance.onend = done;
          utterance.onerror = done;
          setTimeout(done, Math.min(15000, text.length * 80));
          try { window.speechSynthesis.speak(utterance); } catch(err) { done(); }
        } else {
          resolve();
        }
      }
    });
  };

  const toggleTts = () => {
    const newVal = !ttsEnabled;
    setTtsEnabled(newVal);
    if (!newVal) {
      setHideCitizenText(false);
    }
  };

  const fetchRAOScenarios = async () => {
    try {
      const response = await axios.get(`${API}/rao/scenarios`);
      setScenarios(response.data);
    } catch (error) {
      console.error('Error fetching RAO scenarios:', error);
      setScenarios([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRAOSessions = async () => {
    try {
      const response = await axios.get(`${API}/rao/sessions`);
      setRaoSessions(response.data);
    } catch (error) {
      console.error('Error fetching RAO sessions:', error);
    }
  };

  const selectRandomByCategory = (category) => {
    const categoryScenarios = scenarios.filter(s => s.category === category);
    if (categoryScenarios.length === 0) {
      toast.error('Aucun scénario disponible pour cette catégorie');
      return;
    }
    const random = categoryScenarios[Math.floor(Math.random() * categoryScenarios.length)];
    setIsRandomMode(true);
    setRandomCategory(category);
    selectScenario(random);
    const labels = { police: 'Police', ambulance: 'Ambulance', incendie: 'Incendie' };
    toast.success(`Appel aléatoire ${labels[category]} prêt`);
  };

  const selectScenario = (scenario) => {
    setSelectedScenario(scenario);
    setCurrentLineIndex(-1);
    setDisplayedLines([]);
    setIsPlaying(false);
    setCurrentSession(null);
    setSessionCompleted(false);
    if (!scenario) {
      setIsRandomMode(false);
      setRandomCategory(null);
    }
    if (playIntervalRef.current) {
      clearInterval(playIntervalRef.current);
    }
  };

  const startSession = async () => {
    if (!studentName.trim()) {
      toast.error('Veuillez entrer votre nom');
      return;
    }
    
    try {
      // Generate a fresh dialogue variant via AI
      setGeneratingVariant(true);
      let variantDialogue = selectedScenario.dialogue;
      
      try {
        const variantResp = await axios.post(`${API}/rao/generate-variant`, {
          scenario_id: selectedScenario.id,
          difficulty: difficulty,
          student_name: studentName,
          student_number: studentInfo?.student_number || '',
        });
        if (variantResp.data.success && variantResp.data.dialogue) {
          variantDialogue = variantResp.data.dialogue;
          if (variantResp.data.is_variant) {
            toast.success('Nouvelle variante générée');
          }
        }
      } catch (variantError) {
        console.warn('Variant generation failed, using original dialogue:', variantError);
      }
      
      // Update selected scenario with variant dialogue
      setSelectedScenario(prev => ({ ...prev, dialogue: variantDialogue }));
      
      const response = await axios.post(`${API}/rao/sessions`, {
        scenario_id: selectedScenario.id,
        student_name: studentName,
        dialogue: variantDialogue,
        difficulty: difficulty,
        settings: {
          tts_enabled: ttsEnabled,
          hide_citizen_text: hideCitizenText,
          speed: speed,
          difficulty: difficulty,
        }
      });
      
      if (response.data.success) {
        // Store variant dialogue in session too
        const sessionData = { ...response.data.session, dialogue: variantDialogue };
        setCurrentSession(sessionData);
        setSessionStartTime(Date.now());
        setShowNameInput(false);
        setSessionCompleted(false);
        // Reset performance counters
        setPauseCount(0);
        setTotalPauseTime(0);
        setRestartCount(0);
        pauseStartRef.current = null;
        hasStartedRef.current = false;
        toast.success('Session démarrée');
      }
    } catch (error) {
      console.error('Error starting session:', error);
      toast.error('Erreur lors du démarrage de la session');
    } finally {
      setGeneratingVariant(false);
    }
  };

  const startPlayback = () => {
    if (!selectedScenario || !selectedScenario.dialogue) return;
    
    // Track pause end (if resuming from pause)
    if (pauseStartRef.current && hasStartedRef.current) {
      const pauseDuration = Math.floor((Date.now() - pauseStartRef.current) / 1000);
      setTotalPauseTime(prev => prev + pauseDuration);
      pauseStartRef.current = null;
    }
    hasStartedRef.current = true;
    
    // Cancel any pending speech or timeouts
    window.speechSynthesis?.cancel();
    if (advanceTimeoutRef.current) { clearTimeout(advanceTimeoutRef.current); advanceTimeoutRef.current = null; }
    if (playIntervalRef.current) { clearInterval(playIntervalRef.current); playIntervalRef.current = null; }
    
    setIsPlaying(true);
    isPlayingRef.current = true;
    
    let startIndex = currentLineIndex;
    if (currentLineIndex >= selectedScenario.dialogue.length - 1) {
      startIndex = -1;
      setCurrentLineIndex(-1);
      setDisplayedLines([]);
      setSessionCompleted(false);
    }
    
    const dialogue = selectedScenario.dialogue;
    const currentTtsEnabled = ttsEnabled;
    const currentSpeed = speed;
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    const advanceLine = async (fromIndex) => {
      if (!isPlayingRef.current) return;
      
      const nextIndex = fromIndex + 1;
      if (nextIndex >= dialogue.length) {
        setIsPlaying(false);
        isPlayingRef.current = false;
        setSessionCompleted(true);
        autoSaveSession();
        return;
      }
      
      const line = dialogue[nextIndex];
      setDisplayedLines(prev => [...prev, line]);
      setCurrentLineIndex(nextIndex);
      
      if (currentTtsEnabled && line.speaker === 'citizen') {
        if (isMobile) {
          // Mobile: fire-and-forget TTS (may not work), use chosen speed
          try { speakCitizenLine(line.text); } catch(e) { /* ignore */ }
          advanceTimeoutRef.current = setTimeout(() => advanceLine(nextIndex), currentSpeed);
        } else {
          // Desktop: await TTS then short pause
          try {
            await speakCitizenLine(line.text);
          } catch (e) { /* ignore */ }
          if (!isPlayingRef.current) return;
          advanceTimeoutRef.current = setTimeout(() => advanceLine(nextIndex), Math.min(currentSpeed, 1500));
        }
      } else {
        advanceTimeoutRef.current = setTimeout(() => advanceLine(nextIndex), currentSpeed);
      }
    };
    
    advanceLine(startIndex);
  };

  const pausePlayback = () => {
    setIsPlaying(false);
    isPlayingRef.current = false;
    // Track pause start
    if (hasStartedRef.current) {
      setPauseCount(prev => prev + 1);
      pauseStartRef.current = Date.now();
    }
    try { window.speechSynthesis?.cancel(); } catch(e) { /* ignore on mobile */ }
    if (playIntervalRef.current) { clearInterval(playIntervalRef.current); playIntervalRef.current = null; }
    if (advanceTimeoutRef.current) { clearTimeout(advanceTimeoutRef.current); advanceTimeoutRef.current = null; }
  };

  const resetPlayback = () => {
    if (hasStartedRef.current && displayedLines.length > 0) {
      setRestartCount(prev => prev + 1);
    }
    pauseStartRef.current = null;
    pausePlayback();
    setCurrentLineIndex(-1);
    setDisplayedLines([]);
    setSessionCompleted(false);
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      pausePlayback();
    } else {
      startPlayback();
    }
  };

  // Auto-save session when dialogue completes
  const autoSaveSession = async () => {
    if (!currentSession) return;
    
    const endTime = new Date().toISOString();
    const durationSeconds = Math.floor((Date.now() - sessionStartTime) / 1000);
    const dialogueLength = selectedScenario?.dialogue?.length || 0;
    const theoreticalSeconds = Math.floor((dialogueLength * speed) / 1000);
    
    try {
      await axios.put(`${API}/rao/sessions/${currentSession.id}`, {
        status: 'completed',
        end_time: endTime,
        duration_seconds: durationSeconds,
        settings: {
          tts_enabled: ttsEnabled,
          hide_citizen_text: hideCitizenText,
          speed: speed,
          difficulty: difficulty,
        },
        performance: {
          pause_count: pauseCount,
          total_pause_seconds: totalPauseTime,
          restart_count: restartCount,
          theoretical_seconds: theoreticalSeconds,
        }
      });
      
      toast.success('Session sauvegardée automatiquement');
      fetchRAOSessions();
    } catch (error) {
      console.error('Error auto-saving session:', error);
      toast.error('Erreur lors de la sauvegarde automatique');
    }
  };

  // Print current dialogue
  const printDialogue = () => {
    const scenario = selectedScenario;
    const dialogue = displayedLines.length > 0 ? displayedLines : scenario?.dialogue || [];
    
    if (!scenario || dialogue.length === 0) {
      toast.error('Aucun dialogue à imprimer');
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Veuillez autoriser les popups pour imprimer');
      return;
    }

    const categoryLabels = {
      'police': 'Police',
      'ambulance': 'Ambulance',
      'pompier': 'Incendie',
      'incendie': 'Incendie'
    };

    const categoryColors = {
      'police': '#2563eb',
      'ambulance': '#16a34a',
      'pompier': '#dc2626',
      'incendie': '#dc2626'
    };

    const difficultyLabels = { debutant: 'Débutant', intermediaire: 'Intermédiaire', avance: 'Avancé' };
    const diffLabel = difficultyLabels[difficulty] || difficulty;
    const speedLabel = speedOptions.find(s => s.value === speed)?.label || (speed / 1000) + 's';
    const ttsLabel = ttsEnabled ? 'Activée' : 'Désactivée';
    const hideTextLabel = hideCitizenText ? 'Oui' : 'Non';

    // Performance stats
    const durationSec = sessionStartTime ? Math.floor((Date.now() - sessionStartTime) / 1000) : 0;
    const theoreticalSec = Math.floor((dialogue.length * speed) / 1000);
    const formatTime = (s) => Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
    const pauseText = pauseCount + (totalPauseTime > 0 ? ' (' + formatTime(totalPauseTime) + ' cumulé)' : '');

    const performanceHtml = '<div class="meta" style="gap: 20px; flex-wrap: wrap; border-left: 4px solid #f59e0b; background: #fffbeb;">'
      + '<div class="meta-item"><strong>Pauses:</strong> ' + pauseText + '</div>'
      + '<div class="meta-item"><strong>Redémarrages:</strong> ' + restartCount + '</div>'
      + '<div class="meta-item"><strong>Durée réelle:</strong> ' + formatTime(durationSec) + '</div>'
      + '<div class="meta-item"><strong>Temps théorique:</strong> ' + formatTime(theoreticalSec) + '</div>'
      + '</div>';

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>RAO - ${scenario.title}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Arial, sans-serif; 
            padding: 40px;
            color: #1e293b;
            line-height: 1.6;
          }
          .header {
            border-bottom: 3px solid ${categoryColors[scenario.category] || '#0ea5e9'};
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .header h1 {
            font-size: 24px;
            color: #1e293b;
            margin-bottom: 8px;
          }
          .header .category {
            display: inline-block;
            background: ${categoryColors[scenario.category] || '#0ea5e9'};
            color: white;
            padding: 4px 12px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 600;
          }
          .header .description {
            color: #64748b;
            margin-top: 10px;
            font-style: italic;
          }
          .meta {
            display: flex;
            gap: 30px;
            margin-bottom: 30px;
            padding: 15px;
            background: #f8fafc;
            border-radius: 8px;
          }
          .meta-item {
            font-size: 14px;
          }
          .meta-item strong {
            color: #475569;
          }
          .dialogue {
            margin-top: 20px;
          }
          .dialogue-line {
            display: flex;
            margin-bottom: 16px;
            page-break-inside: avoid;
          }
          .speaker {
            width: 100px;
            font-weight: 600;
            flex-shrink: 0;
            padding-top: 2px;
          }
          .speaker.dispatcher {
            color: #0ea5e9;
          }
          .speaker.citizen {
            color: #e11d48;
          }
          .text {
            flex: 1;
            padding: 10px 15px;
            border-radius: 8px;
            background: #f1f5f9;
          }
          .dispatcher-line .text {
            background: #e0f2fe;
          }
          .citizen-line .text {
            background: #fef2f2;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #e2e8f0;
            font-size: 12px;
            color: #94a3b8;
            text-align: center;
          }
          @media print {
            body { padding: 20px; }
            .dialogue-line { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${scenario.title}</h1>
          <span class="category">${categoryLabels[scenario.category] || scenario.category}</span>
          ${scenario.description ? `<p class="description">${scenario.description}</p>` : ''}
        </div>
        
        <div class="meta">
          <div class="meta-item">
            <strong>Étudiant:</strong> ${currentSession?.student_name || studentName || 'N/A'}
          </div>
          <div class="meta-item">
            <strong>Date:</strong> ${new Date().toLocaleDateString('fr-FR', { 
              day: 'numeric', 
              month: 'long', 
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
          <div class="meta-item">
            <strong>Lignes:</strong> ${dialogue.length}
          </div>
        </div>

        <div class="meta" style="gap: 20px; flex-wrap: wrap;">
          <div class="meta-item">
            <strong>Niveau:</strong> ${diffLabel}
          </div>
          <div class="meta-item">
            <strong>Vitesse:</strong> ${speedLabel}
          </div>
          <div class="meta-item">
            <strong>Voix du citoyen:</strong> ${ttsLabel}
          </div>
          ${ttsEnabled ? `<div class="meta-item">
            <strong>Texte citoyen masqué:</strong> ${hideTextLabel}
          </div>` : ''}
        </div>

        ${performanceHtml}

        <div class="dialogue">
          ${dialogue.map((line, index) => `
            <div class="dialogue-line ${line.speaker}-line">
              <div class="speaker ${line.speaker}">
                ${line.speaker === 'dispatcher' ? 'Répartiteur:' : 'Citoyen:'}
              </div>
              <div class="text">${escPrint(line.text)}</div>
            </div>
          `).join('')}
        </div>

        <div class="footer">
          Formation RAO - Simulateur 911 - Imprimé le ${new Date().toLocaleString('fr-FR')}
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Print history session dialogue
  const printHistorySession = (session) => {
    if (!session || !session.dialogue || session.dialogue.length === 0) {
      toast.error('Aucun dialogue à imprimer');
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Veuillez autoriser les popups pour imprimer');
      return;
    }

    const categoryLabels = {
      'police': 'Police',
      'ambulance': 'Ambulance',
      'pompier': 'Incendie',
      'incendie': 'Incendie'
    };

    const categoryColors = {
      'police': '#2563eb',
      'ambulance': '#16a34a',
      'pompier': '#dc2626',
      'incendie': '#dc2626'
    };

    const difficultyLabels = { debutant: 'Débutant', intermediaire: 'Intermédiaire', avance: 'Avancé' };
    const s = session.settings || {};
    const histDiffLabel = difficultyLabels[s.difficulty || session.difficulty] || session.difficulty || 'N/A';
    const histSpeedLabel = s.speed ? (s.speed / 1000) + 's' : 'N/A';
    const histTtsLabel = s.tts_enabled ? 'Activée' : 'Désactivée';
    const histHideLabel = s.hide_citizen_text ? 'Oui' : 'Non';
    const hasSettings = !!session.settings;

    // Performance stats from session
    const p = session.performance || {};
    const histFormatTime = (sec) => sec != null ? Math.floor(sec / 60) + ':' + String(sec % 60).padStart(2, '0') : 'N/A';
    const histPauseCount = p.pause_count ?? 'N/A';
    const histPauseTime = p.total_pause_seconds != null ? histFormatTime(p.total_pause_seconds) : '';
    const histRestarts = p.restart_count ?? 'N/A';
    const histDuration = session.duration_seconds != null ? histFormatTime(session.duration_seconds) : 'N/A';
    const histTheoretical = p.theoretical_seconds != null ? histFormatTime(p.theoretical_seconds) : 'N/A';
    const hasPerf = !!session.performance;

    const histPauseText = histPauseCount + (histPauseTime ? ' (' + histPauseTime + ' cumulé)' : '');
    const histPerfHtml = hasPerf
      ? '<div class="meta" style="gap: 20px; flex-wrap: wrap; border-left: 4px solid #f59e0b; background: #fffbeb;">'
        + '<div class="meta-item"><strong>Pauses:</strong> ' + histPauseText + '</div>'
        + '<div class="meta-item"><strong>Redémarrages:</strong> ' + histRestarts + '</div>'
        + '<div class="meta-item"><strong>Durée réelle:</strong> ' + histDuration + '</div>'
        + '<div class="meta-item"><strong>Temps théorique:</strong> ' + histTheoretical + '</div>'
        + '</div>'
      : '';

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>RAO - ${session.scenario_title}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Arial, sans-serif; 
            padding: 40px;
            color: #1e293b;
            line-height: 1.6;
          }
          .header {
            border-bottom: 3px solid ${categoryColors[session.scenario_category] || '#0ea5e9'};
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .header h1 {
            font-size: 24px;
            color: #1e293b;
            margin-bottom: 8px;
          }
          .header .category {
            display: inline-block;
            background: ${categoryColors[session.scenario_category] || '#0ea5e9'};
            color: white;
            padding: 4px 12px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 600;
          }
          .meta {
            display: flex;
            gap: 30px;
            margin-bottom: 30px;
            padding: 15px;
            background: #f8fafc;
            border-radius: 8px;
          }
          .meta-item {
            font-size: 14px;
          }
          .meta-item strong {
            color: #475569;
          }
          .dialogue {
            margin-top: 20px;
          }
          .dialogue-line {
            display: flex;
            margin-bottom: 16px;
            page-break-inside: avoid;
          }
          .speaker {
            width: 100px;
            font-weight: 600;
            flex-shrink: 0;
            padding-top: 2px;
          }
          .speaker.dispatcher {
            color: #0ea5e9;
          }
          .speaker.citizen {
            color: #e11d48;
          }
          .text {
            flex: 1;
            padding: 10px 15px;
            border-radius: 8px;
            background: #f1f5f9;
          }
          .dispatcher-line .text {
            background: #e0f2fe;
          }
          .citizen-line .text {
            background: #fef2f2;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #e2e8f0;
            font-size: 12px;
            color: #94a3b8;
            text-align: center;
          }
          @media print {
            body { padding: 20px; }
            .dialogue-line { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${session.scenario_title}</h1>
          <span class="category">${categoryLabels[session.scenario_category] || session.scenario_category}</span>
        </div>
        
        <div class="meta">
          <div class="meta-item">
            <strong>Étudiant:</strong> ${session.student_name}
          </div>
          <div class="meta-item">
            <strong>Date:</strong> ${new Date(session.start_time).toLocaleDateString('fr-FR', { 
              day: 'numeric', 
              month: 'long', 
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
          <div class="meta-item">
            <strong>Durée:</strong> ${formatDuration(session.duration_seconds)}
          </div>
        </div>

        <div class="meta" style="gap: 20px; flex-wrap: wrap;">
          <div class="meta-item">
            <strong>Niveau:</strong> ${histDiffLabel}
          </div>
          <div class="meta-item">
            <strong>Vitesse:</strong> ${histSpeedLabel}
          </div>
          <div class="meta-item">
            <strong>Voix du citoyen:</strong> ${histTtsLabel}
          </div>
          ${s.tts_enabled ? `<div class="meta-item">
            <strong>Texte citoyen masqué:</strong> ${histHideLabel}
          </div>` : ''}
        </div>

        ${histPerfHtml}

        <div class="dialogue">
          ${session.dialogue.map((line, index) => `
            <div class="dialogue-line ${line.speaker}-line">
              <div class="speaker ${line.speaker}">
                ${line.speaker === 'dispatcher' ? 'Répartiteur:' : 'Citoyen:'}
              </div>
              <div class="text">${escPrint(line.text)}</div>
            </div>
          `).join('')}
        </div>

        <div class="footer">
          Formation RAO - Simulateur 911 - Imprimé le ${new Date().toLocaleString('fr-FR')}
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm('Supprimer cette session?')) return;
    
    try {
      await axios.delete(`${API}/rao/sessions/${sessionId}`);
      toast.success('Session supprimée');
      fetchRAOSessions();
      if (selectedHistorySession?.id === sessionId) {
        setSelectedHistorySession(null);
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const startNewSession = () => {
    setShowNameInput(true);
    setCurrentSession(null);
    setSessionCompleted(false);
    resetPlayback();
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'police': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'ambulance': return 'bg-green-100 text-green-700 border-green-300';
      case 'incendie': return 'bg-red-100 text-red-700 border-red-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getCategoryLabel = (category) => {
    switch(category) {
      case 'police': return 'Police';
      case 'ambulance': return 'Ambulance';
      case 'incendie': return 'Incendie';
      default: return category;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleSessionSelection = (sessionId) => {
    setSelectedSessionIds(prev => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedSessionIds.size === raoSessions.length) {
      setSelectedSessionIds(new Set());
    } else {
      setSelectedSessionIds(new Set(raoSessions.map(s => s.id)));
    }
  };

  const deleteSelectedSessions = async () => {
    if (selectedSessionIds.size === 0) return;
    const count = selectedSessionIds.size;
    try {
      await Promise.all(
        [...selectedSessionIds].map(id =>
          axios.delete(`${API}/rao/sessions/${id}`)
        )
      );
      setRaoSessions(prev => prev.filter(s => !selectedSessionIds.has(s.id)));
      if (selectedHistorySession && selectedSessionIds.has(selectedHistorySession.id)) {
        setSelectedHistorySession(null);
      }
      setSelectedSessionIds(new Set());
      toast.success(`${count} session${count > 1 ? 's' : ''} supprimée${count > 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error deleting sessions:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredScenarios = serviceFilter === 'all' 
    ? scenarios 
    : scenarios.filter(s => s.category === serviceFilter);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-xl" style={{ color: '#2E6CF5' }}>Chargement...</div>
      </div>
    );
  }

  return (
    <div className="pt-20 px-4 sm:px-6 lg:px-8 pb-12 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-800 mb-2">
            Formation RAO
          </h1>
          <p className="text-lg text-gray-600">
            Répartition Assistée par Ordinateur - Pratiquez la saisie de cartes d'appel
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={startNewSession}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all text-white"
            style={{ backgroundColor: '#2E6CF5' }}
            data-testid="rao-header-new-session"
          >
            <RotateCcw className="w-5 h-5" />
            <span>Nouvelle session</span>
          </button>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
              showHistory 
                ? 'text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            style={showHistory ? { backgroundColor: '#002768' } : {}}
          >
            <History className="w-5 h-5" />
            <span>Historique ({raoSessions.length})</span>
          </button>
        </div>
      </div>

      {/* History Panel */}
      {showHistory && (
        <Card className="glass border-0 mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2">
                <History className="w-5 h-5" style={{ color: '#2E6CF5' }} />
                <span>Historique des sessions RAO</span>
              </CardTitle>
              {raoSessions.length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleSelectAll}
                    className="text-sm px-3 py-1.5 rounded-lg font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    data-testid="rao-select-all"
                  >
                    {selectedSessionIds.size === raoSessions.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                  </button>
                  {selectedSessionIds.size > 0 && (
                    <button
                      onClick={deleteSelectedSessions}
                      className="text-sm px-3 py-1.5 rounded-lg font-medium bg-rose-500 text-white hover:bg-rose-600 transition-colors flex items-center gap-1"
                      data-testid="rao-delete-selected"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Supprimer ({selectedSessionIds.size})
                    </button>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {raoSessions.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Aucune session enregistrée</p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {raoSessions.map((session) => (
                  <div
                    key={session.id}
                    className={`flex items-center justify-between p-3 rounded-lg transition-all ${
                      selectedHistorySession?.id === session.id
                        ? 'border-2'
                        : selectedSessionIds.has(session.id)
                        ? 'bg-rose-50 border-2 border-rose-300'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                    style={selectedHistorySession?.id === session.id ? { backgroundColor: '#CAE9FA', borderColor: '#2E6CF5' } : {}}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <input
                        type="checkbox"
                        checked={selectedSessionIds.has(session.id)}
                        onChange={() => toggleSessionSelection(session.id)}
                        className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                        style={{ accentColor: '#2E6CF5' }}
                        data-testid={`rao-session-checkbox-${session.id}`}
                      />
                      <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{session.student_name}</span>
                        <Badge className={`${getCategoryColor(session.scenario_category)} text-xs`}>
                          {getCategoryLabel(session.scenario_category)}
                        </Badge>
                      </div>
                      <div className="text-sm text-gray-500">
                        {session.scenario_title} • {formatDuration(session.duration_seconds)}
                      </div>
                      <div className="text-xs text-gray-400">
                        {new Date(session.start_time).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setSelectedHistorySession(
                          selectedHistorySession?.id === session.id ? null : session
                        )}
                        className="p-2 rounded-lg hover:bg-blue-50"
                        style={{ color: '#2E6CF5' }}
                        title="Voir le dialogue"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => printHistorySession(session)}
                        className="p-2 rounded-lg hover:bg-green-50"
                        style={{ color: '#24B219' }}
                        title="Imprimer"
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteSession(session.id)}
                        className="p-2 text-rose-500 hover:bg-rose-100 rounded-lg"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Selected session dialogue */}
            {selectedHistorySession && (
              <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold">
                    Dialogue: {selectedHistorySession.scenario_title}
                  </h4>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => printHistorySession(selectedHistorySession)}
                      className="p-2 rounded-lg hover:bg-green-50"
                      style={{ color: '#24B219' }}
                      title="Imprimer"
                    >
                      <Printer className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setSelectedHistorySession(null)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {selectedHistorySession.dialogue?.map((line, idx) => (
                    <div
                      key={idx}
                      className={`flex ${line.speaker === 'dispatcher' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                        line.speaker === 'dispatcher'
                          ? 'text-white'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                      style={line.speaker === 'dispatcher' ? { backgroundColor: '#002768' } : {}}>
                        <span className="font-medium text-xs opacity-70">
                          {line.speaker === 'dispatcher' ? 'Répartiteur' : 'Citoyen'}:
                        </span>
                        <p>{line.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Scenario Selection */}
        <div className="lg:col-span-1">
          <Card className="glass border-0">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Phone className="w-5 h-5" style={{ color: '#2E6CF5' }} />
                <span>Scénarios RAO</span>
              </CardTitle>
              <CardDescription>
                Sélectionnez un scénario à écouter
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Filter buttons */}
              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  onClick={() => setServiceFilter('all')}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                    serviceFilter === 'all' 
                      ? 'bg-gray-800 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Tous
                </button>
                <button
                  onClick={() => setServiceFilter('police')}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                    serviceFilter === 'police' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                  }`}
                >
                  Police
                </button>
                <button
                  onClick={() => setServiceFilter('ambulance')}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                    serviceFilter === 'ambulance' 
                      ? 'bg-green-600 text-white' 
                      : 'bg-green-50 text-green-600 hover:bg-green-100'
                  }`}
                >
                  Ambulance
                </button>
                <button
                  onClick={() => setServiceFilter('incendie')}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                    serviceFilter === 'incendie' 
                      ? 'bg-red-700 text-white' 
                      : 'bg-red-50 text-red-600 hover:bg-red-100'
                  }`}
                >
                  Incendie
                </button>
              </div>

              {/* Random scenario buttons */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => selectRandomByCategory('police')}
                  className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-sm font-semibold text-white transition-all"
                  style={{ backgroundColor: '#002768' }}
                  data-testid="rao-random-police"
                >
                  <Shuffle className="w-3.5 h-3.5" />
                  <span>Police</span>
                </button>
                <button
                  onClick={() => selectRandomByCategory('ambulance')}
                  className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-sm font-semibold text-white transition-all"
                  style={{ backgroundColor: '#24B219' }}
                  data-testid="rao-random-ambulance"
                >
                  <Shuffle className="w-3.5 h-3.5" />
                  <span>Ambulance</span>
                </button>
                <button
                  onClick={() => selectRandomByCategory('incendie')}
                  className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-sm font-semibold text-white transition-all"
                  style={{ backgroundColor: '#DC2626' }}
                  data-testid="rao-random-incendie"
                >
                  <Shuffle className="w-3.5 h-3.5" />
                  <span>Incendie</span>
                </button>
              </div>

              {/* Scenarios list */}
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {filteredScenarios.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>Aucun scénario RAO disponible</p>
                  </div>
                ) : (
                  filteredScenarios.map((scenario) => (
                    <button
                      key={scenario.id}
                      onClick={() => { setIsRandomMode(false); setRandomCategory(null); selectScenario(scenario); }}
                      className={`w-full text-left p-3 rounded-lg transition-all ${
                        !isRandomMode && selectedScenario?.id === scenario.id
                          ? 'border-2'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                      style={!isRandomMode && selectedScenario?.id === scenario.id ? { backgroundColor: '#CAE9FA', borderColor: '#2E6CF5' } : {}}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-800 text-sm">
                          {scenario.title}
                        </span>
                        <Badge className={`${getCategoryColor(scenario.category)} text-xs`}>
                          {getCategoryLabel(scenario.category)}
                        </Badge>
                      </div>
                      {scenario.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {scenario.description}
                        </p>
                      )}
                    </button>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Settings Card */}
          <Card className="glass border-0 mt-4">
            <CardHeader 
              className="cursor-pointer"
              onClick={() => setShowSettings(!showSettings)}
            >
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Settings className="w-5 h-5 text-gray-500" />
                  <span className="text-base">Paramètres</span>
                </div>
                {showSettings ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </CardTitle>
            </CardHeader>
            {showSettings && (
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                      <Clock className="w-4 h-4" />
                      <span>Vitesse de narration</span>
                    </label>
                    <select
                      value={speed}
                      onChange={(e) => setSpeed(Number(e.target.value))}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none"
                      style={{ '--tw-ring-color': '#2E6CF5' }}
                    >
                      {speedOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* TTS Toggle */}
                  <div>
                    <label className="flex items-center justify-between text-sm font-medium text-gray-700" data-testid="tts-toggle-label">
                      <div className="flex items-center space-x-2">
                        {ttsEnabled ? <Volume2 className="w-4 h-4" style={{ color: '#2E6CF5' }} /> : <VolumeX className="w-4 h-4 text-gray-400" />}
                        <span>Voix du citoyen</span>
                      </div>
                      <button
                        onClick={toggleTts}
                        data-testid="tts-toggle-btn"
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          ttsEnabled ? '' : 'bg-gray-300'
                        }`}
                        style={ttsEnabled ? { backgroundColor: '#2E6CF5' } : {}}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          ttsEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">Le système lira les lignes du citoyen à voix haute</p>
                  </div>

                  {/* Hide citizen text toggle - only shown when TTS enabled */}
                  {ttsEnabled && (
                    <div>
                      <label className="flex items-center justify-between text-sm font-medium text-gray-700" data-testid="hide-text-toggle-label">
                        <div className="flex items-center space-x-2">
                          {hideCitizenText ? <EyeOff className="w-4 h-4" style={{ color: '#EE2130' }} /> : <Eye className="w-4 h-4 text-gray-400" />}
                          <span>Masquer texte citoyen</span>
                        </div>
                        <button
                          onClick={() => setHideCitizenText(!hideCitizenText)}
                          data-testid="hide-text-toggle-btn"
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            hideCitizenText ? '' : 'bg-gray-300'
                          }`}
                          style={hideCitizenText ? { backgroundColor: '#EE2130' } : {}}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            hideCitizenText ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </label>
                      <p className="text-xs text-gray-500 mt-1">Mode réaliste : entendez sans voir le texte du citoyen</p>
                    </div>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        </div>

        {/* Right Panel - Dialogue Display */}
        <div className="lg:col-span-2">
          <Card className="glass border-0 h-full">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <Volume2 className="w-5 h-5" style={{ color: '#2E6CF5' }} />
                    <span>Dialogue en cours</span>
                  </CardTitle>
                  {selectedScenario && (
                    <CardDescription className="mt-1">
                      {isRandomMode
                        ? `Appel aléatoire — ${
                            { police: 'Police', ambulance: 'Ambulance', incendie: 'Incendie' }[randomCategory] || ''
                          }`
                        : selectedScenario.title
                      }
                    </CardDescription>
                  )}
                </div>
                
                {/* Playback controls */}
                {selectedScenario && currentSession && (
                  <div className="flex items-center space-x-2">
                    {ttsEnabled && (
                      <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium" 
                        style={{ backgroundColor: '#CAE9FA', color: '#002768' }}
                        data-testid="tts-active-badge"
                      >
                        <Volume2 className="w-3 h-3" />
                        <span>Voix</span>
                        {hideCitizenText && <EyeOff className="w-3 h-3 ml-0.5" />}
                      </div>
                    )}
                    <button
                      onClick={resetPlayback}
                      className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                      title="Recommencer"
                    >
                      <RotateCcw className="w-5 h-5 text-gray-600" />
                    </button>
                    <button
                      onClick={togglePlayPause}
                      disabled={sessionCompleted}
                      className={`p-3 rounded-full transition-colors ${
                        sessionCompleted
                          ? 'bg-gray-300 cursor-not-allowed'
                          : isPlaying 
                            ? 'bg-amber-500 hover:bg-amber-600' 
                            : ''
                      } text-white`}
                      style={!sessionCompleted && !isPlaying ? { backgroundColor: '#2E6CF5' } : {}}
                      title={isPlaying ? 'Pause' : 'Lecture'}
                    >
                      {isPlaying ? (
                        <Pause className="w-6 h-6" />
                      ) : (
                        <Play className="w-6 h-6" />
                      )}
                    </button>
                    <button
                      onClick={printDialogue}
                      className="p-2 rounded-full text-white transition-colors"
                      style={{ backgroundColor: '#24B219' }}
                      title="Imprimer le scénario"
                    >
                      <Printer className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
              
              {/* Progress indicator */}
              {selectedScenario && selectedScenario.dialogue && currentSession && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>Progression</span>
                    <span>{Math.max(0, currentLineIndex + 1)} / {selectedScenario.dialogue.length}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full transition-all duration-300"
                      style={{ 
                        backgroundColor: sessionCompleted ? '#24B219' : '#2E6CF5',
                        width: `${selectedScenario.dialogue.length > 0 
                          ? ((currentLineIndex + 1) / selectedScenario.dialogue.length) * 100 
                          : 0}%` 
                      }}
                    />
                  </div>
                  {sessionCompleted && (
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-sm font-medium" style={{ color: '#24B219' }}>
                        ✓ Session terminée et sauvegardée
                      </span>
                      <button
                        onClick={startNewSession}
                        className="text-sm font-medium hover:underline"
                        style={{ color: '#2E6CF5' }}
                      >
                        Nouvelle session
                      </button>
                    </div>
                  )}
                </div>
              )}
            </CardHeader>
            
            <CardContent>
              {!selectedScenario ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                  <Phone className="w-16 h-16 mb-4 text-gray-300" />
                  <p className="text-lg">Sélectionnez un scénario pour commencer</p>
                  <p className="text-sm mt-2">Les dialogues apparaîtront ici</p>
                </div>
              ) : showNameInput ? (
                <div className="flex flex-col items-center justify-center py-8 px-4">
                  <User className="w-12 h-12 mb-3" style={{ color: '#CAE9FA' }} />
                  <h3 className="text-xl font-semibold mb-6" style={{ color: '#002768' }}>
                    Configuration de la session
                  </h3>
                  <div className="w-full max-w-md space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Votre nom</label>
                      <input
                        type="text"
                        value={studentName}
                        onChange={(e) => setStudentName(e.target.value)}
                        placeholder="Votre nom..."
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none"
                        style={{ '--tw-ring-color': '#2E6CF5' }}
                        onKeyDown={(e) => e.key === 'Enter' && studentName.trim() && startSession()}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Niveau de difficulté</label>
                      <div className="grid grid-cols-3 gap-2">
                        <button
                          onClick={() => allowedLevels.includes('debutant') && setDifficulty('debutant')}
                          data-testid="difficulty-debutant"
                          disabled={!allowedLevels.includes('debutant')}
                          className={`p-3 rounded-lg border-2 text-center transition-all ${
                            !allowedLevels.includes('debutant')
                              ? 'border-gray-200 bg-gray-100 opacity-40 cursor-not-allowed'
                              : difficulty === 'debutant'
                                ? 'border-2'
                                : 'border-gray-200 hover:border-green-200'
                          }`}
                          style={allowedLevels.includes('debutant') && difficulty === 'debutant' ? { borderColor: '#24B219', backgroundColor: '#E0F8E0' } : {}}
                        >
                          <div className="text-2xl mb-1">1</div>
                          <div className="font-semibold text-sm" style={{ color: allowedLevels.includes('debutant') ? '#24B219' : '#9CA3AF' }}>Débutant</div>
                          <p className="text-xs text-gray-500 mt-1">Adresse + Nature</p>
                          {!allowedLevels.includes('debutant') && <p className="text-[10px] text-gray-400 mt-1">Non autorisé</p>}
                        </button>
                        <button
                          onClick={() => allowedLevels.includes('intermediaire') && setDifficulty('intermediaire')}
                          data-testid="difficulty-intermediaire"
                          disabled={!allowedLevels.includes('intermediaire')}
                          className={`p-3 rounded-lg border-2 text-center transition-all ${
                            !allowedLevels.includes('intermediaire')
                              ? 'border-gray-200 bg-gray-100 opacity-40 cursor-not-allowed'
                              : difficulty === 'intermediaire'
                                ? 'border-amber-500 bg-amber-50'
                                : 'border-gray-200 hover:border-amber-300'
                          }`}
                        >
                          <div className="text-2xl mb-1">2</div>
                          <div className={`font-semibold text-sm ${allowedLevels.includes('intermediaire') ? 'text-amber-700' : 'text-gray-400'}`}>Intermédiaire</div>
                          <p className="text-xs text-gray-500 mt-1">+ Nom, Tél, Conclusion</p>
                          {!allowedLevels.includes('intermediaire') && <p className="text-[10px] text-gray-400 mt-1">Non autorisé</p>}
                        </button>
                        <button
                          onClick={() => allowedLevels.includes('avance') && setDifficulty('avance')}
                          data-testid="difficulty-avance"
                          disabled={!allowedLevels.includes('avance')}
                          className={`p-3 rounded-lg border-2 text-center transition-all ${
                            !allowedLevels.includes('avance')
                              ? 'border-gray-200 bg-gray-100 opacity-40 cursor-not-allowed'
                              : difficulty === 'avance'
                                ? 'border-rose-500 bg-rose-50'
                                : 'border-gray-200 hover:border-rose-300'
                          }`}
                        >
                          <div className="text-2xl mb-1">3</div>
                          <div className={`font-semibold text-sm ${allowedLevels.includes('avance') ? 'text-rose-700' : 'text-gray-400'}`}>Avancé</div>
                          <p className="text-xs text-gray-500 mt-1">Scénario complet</p>
                          {!allowedLevels.includes('avance') && <p className="text-[10px] text-gray-400 mt-1">Non autorisé</p>}
                        </button>
                      </div>
                    </div>

                    <button
                      onClick={startSession}
                      disabled={!studentName.trim() || generatingVariant}
                      className="w-full py-3 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      style={{ background: 'linear-gradient(to right, #002768, #2E6CF5)' }}
                    >
                      {generatingVariant ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                          Génération du dialogue...
                        </span>
                      ) : (
                        'Commencer la session'
                      )}
                    </button>
                  </div>
                </div>
              ) : displayedLines.length === 0 && !isPlaying ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                  <Play className="w-16 h-16 mb-4" style={{ color: '#CAE9FA' }} />
                  <p className="text-lg">Appuyez sur lecture pour commencer</p>
                  <p className="text-sm mt-2">Vitesse: {speedOptions.find(o => o.value === speed)?.label}</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[500px] overflow-y-auto p-2">
                  {displayedLines.map((line, index) => (
                    <div
                      key={index}
                      className={`flex ${line.speaker === 'dispatcher' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`flex items-start space-x-3 max-w-[80%] ${
                          line.speaker === 'dispatcher' ? 'flex-row-reverse space-x-reverse' : ''
                        }`}
                      >
                        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-white"
                          style={{ backgroundColor: line.speaker === 'dispatcher' ? '#002768' : '#EE2130' }}>
                          {line.speaker === 'dispatcher' ? (
                            <User className="w-5 h-5" />
                          ) : (
                            <Bot className="w-5 h-5" />
                          )}
                        </div>
                        <div>
                          <div className={`px-4 py-3 rounded-2xl ${
                            line.speaker === 'dispatcher'
                              ? 'text-white rounded-br-md'
                              : 'bg-gray-100 text-gray-800 rounded-bl-md'
                          }`}
                          style={line.speaker === 'dispatcher' ? { backgroundColor: '#002768' } : {}}>
                            {line.speaker === 'citizen' && hideCitizenText && ttsEnabled ? (
                              <p className="text-sm leading-relaxed italic flex items-center gap-2 text-gray-500">
                                <Volume2 className="w-4 h-4 flex-shrink-0" />
                                <span>Audio seulement</span>
                              </p>
                            ) : (
                              <p className="text-sm leading-relaxed">{line.text}</p>
                            )}
                          </div>
                          <div className={`text-xs text-gray-500 mt-1 ${
                            line.speaker === 'dispatcher' ? 'text-right' : 'text-left'
                          }`}>
                            {line.speaker === 'dispatcher' ? 'Répartiteur' : 'Citoyen'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Completion actions panel - visible at bottom of dialogue */}
                  {sessionCompleted && (
                    <div className="mt-6 p-5 border-2 rounded-xl text-center" style={{ backgroundColor: '#E0F8E0', borderColor: '#24B219' }} data-testid="rao-completion-panel">
                      <p className="font-semibold mb-4" style={{ color: '#24B219' }}>Session terminée et sauvegardée</p>
                      <div className="flex flex-wrap items-center justify-center gap-3">
                        <button
                          onClick={() => { resetPlayback(); setShowNameInput(true); }}
                          disabled={generatingVariant}
                          className="flex items-center space-x-2 px-4 py-2 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                          style={{ backgroundColor: '#2E6CF5' }}
                          data-testid="rao-replay-btn"
                        >
                          <Shuffle className="w-4 h-4" />
                          <span>Nouvelle variante</span>
                        </button>
                        <button
                          onClick={() => { resetPlayback(); startPlayback(); }}
                          className="flex items-center space-x-2 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                          data-testid="rao-same-replay-btn"
                        >
                          <RotateCcw className="w-4 h-4" />
                          <span>Même dialogue</span>
                        </button>
                        <button
                          onClick={() => { selectScenario(null); startNewSession(); }}
                          className="flex items-center space-x-2 px-4 py-2 text-white rounded-lg font-medium transition-colors"
                          style={{ backgroundColor: '#002768' }}
                          data-testid="rao-new-scenario-btn"
                        >
                          <Phone className="w-4 h-4" />
                          <span>Autre scénario</span>
                        </button>
                        <button
                          onClick={printDialogue}
                          className="flex items-center space-x-2 px-4 py-2 text-white rounded-lg font-medium transition-colors"
                          style={{ backgroundColor: '#24B219' }}
                          data-testid="rao-print-btn"
                        >
                          <Printer className="w-4 h-4" />
                          <span>Imprimer</span>
                        </button>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RAOTraining;
